import { useEffect } from "react";
import { matchPath, useLocation } from "react-router";

import { getUrlWithStoredParams } from "@/components/Routing/redirectFromLoader";
import { clearSessionStorage, getSessionStorageValue } from "@/hooks/useLocalStorage";
import { useSlug } from "@/hooks/useSlug";
import { ticketStorageKeys } from "@/queries/tickets/filters";
import { routes } from "@/routes";

import type { TicketsRouteParams } from "./routes";

const TICKET_FILTER_PARAM_KEYS: Array<keyof TicketsRouteParams["list"]> = [
  "tab",
  "addressTypes",
  "assigneeIds",
  "assigneeMode",
  "categoryIds",
  "visibility",
  "ratings",
  "statusIds",
  "closedFrom",
  "closedTo",
  "ratedFrom",
  "ratedTo",
  "isUnreadOnly",
];

/**
 * The admin ticket list URL with the filters stored earlier in this tab, when the URL has none of its own.
 * The route loader redirects there before the page renders, so the page only queries the filtered list.
 */
export function getStoredTicketFiltersUrl(url: URL, slug: string): string | undefined {
  const stored = getSessionStorageValue<Partial<TicketsRouteParams["list"]>>(ticketStorageKeys.filters(slug), {});

  return getUrlWithStoredParams(url, TICKET_FILTER_PARAM_KEYS, stored);
}

function clearTicketsStorage(slug: string): void {
  clearSessionStorage(ticketStorageKeys.residentFocusMode());
  clearSessionStorage(ticketStorageKeys.filters(slug));
}

// Used for only clearing ticket-related session storage when navigating away from ticket-related pages
export function useClearTicketsCache(): void {
  const slug = useSlug();
  const location = useLocation();

  useEffect(() => {
    if (matchPath(routes.tickets.ROOT, location.pathname)) {
      return;
    }

    if (matchPath(routes.ticketCategories.ROOT, location.pathname)) {
      return;
    }

    if (matchPath(routes.ticketStatuses.ROOT, location.pathname)) {
      return;
    }

    if (matchPath(routes.users.details.PATH, location.pathname)) {
      return;
    }

    clearTicketsStorage(slug);
  }, [location, slug]);
}
